import { useApolloClient } from '@apollo/client'
import {
  Checkbox,
  Dialog,
  DialogMaxWidth,
  DialogVariant,
  Grid,
  RadioGroup,
  Select,
  SelectVariant,
  Text,
  TextHighlight,
  TextSize
} from '@focaldata/cin-ui-components'
import React, { useEffect, useState } from 'react'
import useDownloadSurveyDoc, {
  ExportTarget
} from '../../../../hooks/useDownloadSurveyDoc'
import { useProjectData } from '../../../../modules/Project/Project.hooks'
import { useProjectMarkets } from '../../../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import {
  downloadAllMasterFilename,
  downloadAllZipFilename,
  marketZipFilename
} from '../../../../utils/surveyDocBuilder/buildSurveyDoc'
import { downloadAllTargets, loadMarketLanguages } from './downloadAllTargets'
import {
  MASTER_LANGUAGE,
  pickLanguageOption,
  useMarketLanguageOptions,
  useTranslatedLanguageOptions
} from './exportLanguageOptions'

export enum ExportSurveyMode {
  Master = 'master',
  Market = 'market',
  Language = 'language',
  All = 'all'
}

interface Props {
  open: boolean
  onClose: () => void
  // The language the caller is currently showing (the results pages); used
  // as the Language-specific default when it has stored translations.
  defaultLanguage?: string
  // Opens in Market-specific mode with this market picked (the per-market
  // results page). Otherwise the dialog opens on Master survey.
  initialMarketSurveyId?: string
  multiMarket?: boolean
  // Whether the Master and Language docs list screeners. A project with
  // markets (multi-market or tracker) leaves them out: each market has its own.
  sharedDocsIncludeScreeners?: boolean
  // Holds Export until the project type, and so the screener choice, is known.
  projectTypeLoading?: boolean
}

const MODE_ITEMS = [
  { value: ExportSurveyMode.Master, label: 'Master survey' },
  { value: ExportSurveyMode.Market, label: 'Market-specific' },
  { value: ExportSurveyMode.Language, label: 'Language-specific' },
  { value: ExportSurveyMode.All, label: 'Download all (.zip)' }
]

// Only custom screeners carry translations; standard ones print master text.
const modeCaption = (
  mode: ExportSurveyMode,
  { multiMarket, screeners }: { multiMarket: boolean; screeners: boolean }
): string => {
  switch (mode) {
    case ExportSurveyMode.Master:
      return `All questions in the master language${
        multiMarket ? ', with market visibility notes' : ''
      }. ${screeners ? 'Includes screeners.' : 'No screeners.'}`
    case ExportSurveyMode.Language:
      return `All questions in one language, falling back to master text where translations are missing. ${
        screeners
          ? 'Includes screeners; standard screeners stay in the master language.'
          : 'No screeners.'
      }`
    case ExportSurveyMode.Market:
      return 'Visible questions and screeners, with master question numbers. Audience/screener numbers (A1, A2…) are per market and differ between markets. One document per language; multiple download as a zip.'
    case ExportSurveyMode.All:
      return 'The master survey and every market in its fieldwork languages, together in one zip.'
  }
}

const languageCodeOf = (code: string) =>
  code === MASTER_LANGUAGE ? undefined : code

/**
 * One export dialog for multi-market or multilingual projects. The market decides what is
 * included; the language decides which text is shown. Every export is strict:
 * a failed load keeps the dialog open with the error instead of saving an
 * empty document.
 */
const ExportSurveyDialog: React.FC<Props> = ({
  open,
  onClose,
  defaultLanguage,
  initialMarketSurveyId,
  multiMarket = true,
  sharedDocsIncludeScreeners = !multiMarket,
  projectTypeLoading = false
}) => {
  const client = useApolloClient()
  const { exportDocuments } = useDownloadSurveyDoc()
  const { project } = useProjectData()
  const markets = useProjectMarkets()

  const initialMode =
    multiMarket && initialMarketSurveyId
      ? ExportSurveyMode.Market
      : ExportSurveyMode.Master
  const [mode, setMode] = useState<ExportSurveyMode>(initialMode)
  // Only explicit picks are held in state, so defaults that resolve late
  // (markets, languages) apply until the user chooses otherwise.
  const [pickedMarketId, setPickedMarketId] = useState<string>()
  const [pickedLanguage, setPickedLanguage] = useState<string>()
  // Unchecked rather than checked, so every language of the market starts
  // checked even when its list arrives after the market is picked.
  const [unchecked, setUnchecked] = useState<Set<string>>(new Set())
  const [progress, setProgress] = useState<{ current: number; total: number }>()
  const [error, setError] = useState<string>()

  // The dialog stays mounted, so reset it on every open.
  useEffect(() => {
    if (open) {
      setMode(initialMode)
      setPickedMarketId(undefined)
      setPickedLanguage(undefined)
      setUnchecked(new Set())
      setProgress(undefined)
      setError(undefined)
    }
  }, [open, initialMode])

  const market =
    markets.find(
      (option) => option.surveyId === (pickedMarketId ?? initialMarketSurveyId)
    ) ?? markets.at(0)

  const marketLanguages = useMarketLanguageOptions(
    open && mode === ExportSurveyMode.Market ? market?.surveyId : undefined
  )
  const translatedLanguages = useTranslatedLanguageOptions(
    open && mode === ExportSurveyMode.Language
  )
  const selectedLanguage = pickLanguageOption(
    translatedLanguages.options,
    pickedLanguage ?? defaultLanguage ?? MASTER_LANGUAGE
  )
  const checkedLanguages = marketLanguages.options
    .map((option) => option.value)
    .filter((code) => !unchecked.has(code))

  const exporting = Boolean(progress)
  const projectName = project?.name ?? 'Survey'

  const targets = ((): ExportTarget[] => {
    switch (mode) {
      case ExportSurveyMode.Master:
      case ExportSurveyMode.All:
        return [{}]
      case ExportSurveyMode.Language:
        return [{ languageCode: languageCodeOf(selectedLanguage) }]
      case ExportSurveyMode.Market:
        return market
          ? checkedLanguages.map((code) => ({
              market,
              languageCode: languageCodeOf(code)
            }))
          : []
    }
  })()

  const optionsBlocked =
    mode === ExportSurveyMode.Market
      ? !market || marketLanguages.loading || marketLanguages.failed
      : mode === ExportSurveyMode.Language
        ? translatedLanguages.loading || translatedLanguages.failed
        : false

  const handleExport = async () => {
    setError(undefined)
    setProgress({
      current: 1,
      total: mode === ExportSurveyMode.All ? 0 : targets.length
    })
    try {
      let exportTargets = targets
      if (mode === ExportSurveyMode.All) {
        const languages = await loadMarketLanguages(client, markets)
        const [master, ...rest] = downloadAllTargets(markets, languages)
        exportTargets = [
          { ...master, filename: downloadAllMasterFilename(projectName) },
          ...rest
        ]
      }
      // The caption's screener promise and the docs come from the same flag.
      exportTargets = exportTargets.map((target) => ({
        ...target,
        includeAudienceQuestions:
          Boolean(target.market) || sharedDocsIncludeScreeners
      }))
      await exportDocuments(exportTargets, {
        zipName:
          mode === ExportSurveyMode.All
            ? downloadAllZipFilename(projectName)
            : market
              ? marketZipFilename(projectName, market)
              : `${projectName}.zip`,
        onProgress: (current, total) => setProgress({ current, total })
      })
      setProgress(undefined)
      onClose()
    } catch (e) {
      setProgress(undefined)
      setError(
        `Couldn’t export the survey${
          e instanceof Error && e.message ? `: ${e.message}` : ''
        }. Please try again.`
      )
    }
  }

  const handleClose = () => {
    if (!exporting) onClose()
  }

  const toggleLanguage = (code: string) => {
    setUnchecked((previous) => {
      const next = new Set(previous)
      if (next.has(code)) {
        next.delete(code)
      } else {
        next.add(code)
      }
      return next
    })
  }

  const languagesCaption = (): string | undefined => {
    const languages =
      mode === ExportSurveyMode.Market ? marketLanguages : translatedLanguages
    if (languages.failed) {
      return 'Couldn’t load the available languages. Close the dialog and try again.'
    }
    if (languages.loading) {
      return 'Loading languages…'
    }
    return undefined
  }
  const statusCaption = languagesCaption()

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Export survey"
      variant={DialogVariant.DoubleButton}
      maxWidth={DialogMaxWidth.Sm}
      primaryButtonText={
        progress
          ? progress.total
            ? `Building ${progress.current} of ${progress.total}…`
            : 'Preparing survey documents…'
          : 'Export'
      }
      primaryButtonDisabled={
        exporting ||
        projectTypeLoading ||
        optionsBlocked ||
        targets.length === 0
      }
      primaryButtonLoading={exporting}
      primaryButtonClick={() => void handleExport()}
      secondaryButtonText="Cancel"
      secondaryButtonDisabled={exporting}
      secondaryButtonClick={handleClose}
    >
      <Grid container direction="column" spacing={2} style={{ padding: 16 }}>
        <Grid item>
          <fieldset
            disabled={exporting}
            style={{ border: 0, margin: 0, padding: 0 }}
          >
            <RadioGroup
              value={mode}
              items={MODE_ITEMS.filter(
                ({ value }) =>
                  multiMarket ||
                  (value !== ExportSurveyMode.Market &&
                    value !== ExportSurveyMode.All)
              )}
              onChange={(event) => {
                setMode(event.target.value as ExportSurveyMode)
                setError(undefined)
              }}
            />
          </fieldset>
        </Grid>
        <Grid item>
          <Text size={TextSize.s}>
            {modeCaption(mode, {
              multiMarket,
              screeners: sharedDocsIncludeScreeners
            })}
          </Text>
        </Grid>
        <Grid item>
          <Text size={TextSize.s}>
            To edit wording or translations, use the Language-specific export
            and re-import it.{' '}
            {multiMarket && 'Market documents can’t be re-imported.'}
          </Text>
        </Grid>

        {mode === ExportSurveyMode.Market && (
          <>
            <Grid item>
              <Select
                variant={SelectVariant.Outlined}
                options={markets.map((option) => ({
                  id: option.surveyId,
                  name: option.flag
                    ? `${option.flag} ${option.label}`
                    : option.label,
                  value: option.surveyId
                }))}
                optionValue={market?.surveyId ?? ''}
                ariaLabel="Export market"
                disabled={exporting}
                onChange={(event) => {
                  setPickedMarketId(event.target.value as string)
                  setUnchecked(new Set())
                }}
              />
            </Grid>
            <Grid item>
              {marketLanguages.options.map((option) => (
                <div key={option.value}>
                  <Checkbox
                    label={option.name}
                    ariaLabel={option.name}
                    checked={!unchecked.has(option.value)}
                    disabled={exporting || marketLanguages.loading}
                    onChange={() => toggleLanguage(option.value)}
                  />
                </div>
              ))}
              {!marketLanguages.loading && checkedLanguages.length === 0 && (
                <Text size={TextSize.s} highlight={TextHighlight.Error}>
                  Select at least one language.
                </Text>
              )}
            </Grid>
          </>
        )}

        {mode === ExportSurveyMode.Language && (
          <Grid item>
            <Select
              variant={SelectVariant.Outlined}
              options={translatedLanguages.options}
              optionValue={selectedLanguage}
              ariaLabel="Export language"
              disabled={exporting}
              onChange={(event) =>
                setPickedLanguage(event.target.value as string)
              }
            />
          </Grid>
        )}

        {statusCaption &&
          (mode === ExportSurveyMode.Market ||
            mode === ExportSurveyMode.Language) && (
            <Grid item>
              <Text size={TextSize.s}>{statusCaption}</Text>
            </Grid>
          )}
        {error && (
          <Grid item>
            <Text size={TextSize.s} highlight={TextHighlight.Error}>
              {error}
            </Text>
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}

export default ExportSurveyDialog
